import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '@/App';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  MapPin,
  Box,
  Package,
  AlertTriangle,
  TrendingUp,
  Clock,
  Bell,
  BellOff,
  Euro,
  ShoppingCart
} from 'lucide-react';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import QRScanner from '@/components/QRScanner';
import { useNavigate } from 'react-router-dom';
import ScanButton from '@/components/ScanButton';
import UnknownCodeDialog from '@/components/UnknownCodeDialog';
import { resolveScan, ScanResultType, ScanContext } from '@/utils/scanResolver';
import DashboardCard from '@/components/DashboardCard';

const DashboardPage = () => {
  const { user, API } = useContext(AuthContext);
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [lowStockItems, setLowStockItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snoozedLocations, setSnoozedLocations] = useState({});
  const [showScanner, setShowScanner] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [scannedCode, setScannedCode] = useState('');

  useEffect(() => {
    fetchData();
    const snoozeData = localStorage.getItem('snoozed_locations');
    if (snoozeData) {
      setSnoozedLocations(JSON.parse(snoozeData));
    }
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, tasksRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/dashboard/tasks`),
      ]);
      setStats(statsRes.data);
      setTasks(tasksRes.data || []);

      // Lade Nachbestellhinweise nur für Admin
      if (user?.role === 'admin') {
        try {
          const lowStockRes = await axios.get(`${API}/inventory/low-stock`);
          setLowStockItems(lowStockRes.data || []);
        } catch (e) {
          // Silently ignore if endpoint not available
          setLowStockItems([]);
        }
      }

      // Check for overdue (Audio deaktiviert in v1.5)
      // const overdue = (tasksRes.data || []).filter(item => {
      //   const locationId = item.standort?.id || item.machine?.id;
      //   return item.is_overdue && !isSnoozed(locationId);
      // });
    } catch (error) {
      // Nur bei echten Serverfehlern einen Toast zeigen
      if (error.response?.status >= 500) {
        toast.error('Serverfehler beim Laden der Dashboard-Daten');
      } else if (error.response?.status === 403) {
        toast.error('Keine Berechtigung für Dashboard');
      }
      // Bei anderen Fehlern: Default-Werte setzen ohne Toast
      setStats(null);
      setTasks([]);
      console.error('Dashboard laden:', error.response?.status || error.message);
    } finally {
      setLoading(false);
    }
  };

  // Audio deaktiviert in v1.5 - keine Sounds mehr
  // const playAlarmSound = () => { ... };

  const isSnoozed = (locationId) => {
    const snoozeUntil = snoozedLocations[locationId];
    if (!snoozeUntil) return false;
    return new Date(snoozeUntil) > new Date();
  };

  const snoozeLocation = (locationId, days) => {
    const snoozeUntil = new Date();
    snoozeUntil.setDate(snoozeUntil.getDate() + days);
    const newSnoozed = { ...snoozedLocations, [locationId]: snoozeUntil.toISOString() };
    setSnoozedLocations(newSnoozed);
    localStorage.setItem('snoozed_locations', JSON.stringify(newSnoozed));
    toast.success(`Standort für ${days} Tag(e) stummgeschaltet`);
  };

  const unsnoozeLocation = (locationId) => {
    const newSnoozed = { ...snoozedLocations };
    delete newSnoozed[locationId];
    setSnoozedLocations(newSnoozed);
    localStorage.setItem('snoozed_locations', JSON.stringify(newSnoozed));
    toast.success('Stummschaltung aufgehoben');
  };

  const handleQRScan = async (qrValue) => {
    setShowScanner(false);
    
    // Normalisiere den gescannten Code (nur trim, KEIN parseInt - führende Nullen behalten!)
    const normalizedCode = qrValue.trim();
    setScannedCode(normalizedCode);
    
    console.log(`[Dashboard-Scan] Gescannter Code: '${normalizedCode}'`);

    try {
      // NUTZE ZENTRALEN RESOLVER für konsistente Identifikation
      const result = await resolveScan({
        qrValue: normalizedCode,
        api: API,
        user,
        context: ScanContext.UNIVERSAL
      });

      console.log(`[Dashboard-Scan] Resolver-Ergebnis:`, result);

      // Fall 1: Standort gefunden
      if (result.type === ScanResultType.STANDORT) {
        toast.success(result.message);
        navigate(result.navigateTo);
        return;
      }
      
      // Fall 2: Automat gefunden
      if (result.type === ScanResultType.AUTOMAT) {
        toast.success(result.message);
        navigate(result.navigateTo);
        return;
      }
      
      // Fall 3: Produkt gefunden
      if (result.type === ScanResultType.PRODUKT) {
        toast.success(result.message);
        navigate(result.navigateTo);
        return;
      }
      
      // Fall 4: Fehler (z.B. Berechtigung)
      if (result.type === ScanResultType.ERROR) {
        toast.error(result.message);
        return;
      }
      
      // Fall 5: Nicht gefunden - zeige Erstellen-Dialog
      if (result.type === ScanResultType.UNKNOWN) {
        console.log(`[Dashboard-Scan] Code nicht gefunden, zeige Erstellen-Dialog`);
        setShowCreateDialog(true);
      }
    } catch (error) {
      console.error('[Dashboard-Scan] Fehler:', error);
      toast.error('Fehler beim Scannen');
    }
  };

  const handleCreateNew = (type) => {
    setShowCreateDialog(false);
    if (type === 'standort') {
      navigate('/standorte', { state: { openCreate: true, scannedCode: scannedCode } });
    } else {
      navigate('/automaten', { state: { openCreate: true, scannedCode: scannedCode } });
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64">Laden...</div>;
  }

  if (!stats) {
    return <div className="flex justify-center items-center h-64">Fehler beim Laden der Daten</div>;
  }

  return (
    <div className="space-y-4 sm:space-y-6 lg:space-y-8 animate-slide-in" data-testid="dashboard-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4">
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-1 sm:mb-2" style={{ fontFamily: 'Space Grotesk' }}>
            Dashboard
          </h1>
          <p className="text-sm sm:text-base text-slate-600">Übersicht über deine Kaugummiautomaten</p>
        </div>
        <ScanButton
          onClick={() => setShowScanner(true)}
          size="lg"
          className="flex-shrink-0"
          data-testid="quick-scan-button"
        />
      </div>

      {/* Stats Grid - Responsive mit besseren Breakpoints */}
      <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-4 lg:gap-6 auto-rows-fr">
        {user?.role === 'admin' && (
          <DashboardCard
            title="Aktive Standorte"
            value={stats.active_locations}
            icon={MapPin}
            colorScheme="blue"
            href="/standorte"
            testId="stat-active-locations"
          />
        )}

        {user?.role === 'admin' && (
          <DashboardCard
            title="AKTIV"
            value={stats.machines_in_housing}
            icon={Box}
            colorScheme="green"
            href="/automaten"
            testId="stat-machines-housing"
          />
        )}

        <DashboardCard
          title="Lager VOLL"
          value={stats.machines_stock_filled}
          icon={Package}
          colorScheme="purple"
          href="/automaten"
          testId="stat-machines-filled"
        />

        <DashboardCard
          title="Lager LEER"
          value={stats.machines_stock_empty}
          icon={Box}
          colorScheme="slate"
          href="/automaten"
          testId="stat-machines-empty"
        />

        <DashboardCard
          title="Defekte Automaten"
          value={stats.machines_defect}
          icon={AlertTriangle}
          colorScheme="red"
          href="/automaten"
          testId="stat-machines-defect"
        />

        {user?.role === 'admin' && stats.defect_locations !== undefined && (
          <DashboardCard
            title="Defekte Standorte"
            value={stats.defect_locations}
            icon={MapPin}
            colorScheme="orange"
            href="/standorte"
            testId="stat-defect-locations"
          />
        )}

        {user?.role === 'admin' && stats.total_revenue !== undefined && (
          <DashboardCard
            title={`Gesamtumsatz ${new Date().getFullYear()}`}
            value={`${stats.total_revenue.toFixed(2)} €`}
            icon={TrendingUp}
            colorScheme="amber"
            href="/reports"
            testId="stat-total-revenue"
          />
        )}
      </div>

      {/* Tasks List */}
      <Card className="p-6" data-testid="tasks-section">
        <div className="flex items-center space-x-3 mb-6">
          <Clock className="text-blue-600" size={28} />
          <div>
            <h2 className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>
              Aufgaben
            </h2>
            <p className="text-sm text-slate-600">
              {user?.role === 'admin' ? 'Befüllung, Reparaturen, Schäden' : 'Automaten-Aufgaben'}
            </p>
          </div>
        </div>

        <div className="space-y-3">
          {/* Nachbestellhinweise - nur für Admin */}
          {user?.role === 'admin' && lowStockItems.length > 0 && (
            <div className="p-4 rounded-xl border-2 bg-orange-50 border-orange-300 mb-4" data-testid="low-stock-section">
              <div className="flex items-center space-x-3 mb-3">
                <ShoppingCart className="text-orange-600" size={20} />
                <h3 className="font-bold text-lg text-orange-700">Artikel nachbestellen</h3>
                <span className="bg-orange-200 text-orange-800 text-xs px-2 py-1 rounded-full font-semibold">
                  {lowStockItems.length}
                </span>
              </div>
              <div className="space-y-2">
                {lowStockItems.map((item) => (
                  <Link
                    key={item.id}
                    to={`/inventar?highlight=${item.id}`}
                    className="block p-3 bg-white rounded-lg border border-orange-200 hover:bg-orange-100 transition-colors"
                    data-testid={`low-stock-item-${item.code}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="font-medium text-slate-800">{item.name}</span>
                        <span className="text-slate-500 ml-2 text-sm">({item.code})</span>
                      </div>
                      <div className="text-right">
                        <span className={`font-bold ${item.quantity <= 0 ? 'text-red-600' : 'text-orange-600'}`}>
                          Bestand: {item.quantity}
                        </span>
                        <span className="text-slate-500 ml-2 text-sm">
                          / Min: {item.min_stock}
                        </span>
                      </div>
                    </div>
                    {item.storage_location_type && (
                      <p className="text-xs text-slate-500 mt-1">
                        Lagerort: {item.storage_location_type}
                      </p>
                    )}
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* P3: Helfer sehen keine Standort-Tasks und keine Auszähl-Tasks */}
          {(() => {
            const isAdmin = user?.role === 'admin';
            // Helfer: Nur Automaten-Defekt-Tasks und Reparatur-Tasks (ohne Standort-bezogene)
            const filteredTasks = isAdmin 
              ? tasks 
              : tasks.filter(item => 
                  item.type !== 'automat_auszaehlen' && // Keine Auszähl-Tasks
                  (item.type === 'automat_defekt' || item.needs_repair) // Nur Automaten-Tasks
                );
            
            if (filteredTasks.length === 0 && (user?.role !== 'admin' || lowStockItems.length === 0)) {
              return <p className="text-slate-500 text-center py-8">Keine offenen Aufgaben!</p>;
            }
            
            if (filteredTasks.length === 0) {
              return null; // Low-stock items are shown above
            }
            
            return filteredTasks.map((item) => {
              const locationId = item.standort?.id || item.machine?.id;
              const snoozed = isSnoozed(locationId);
              
              // Aktiver Schaden: Nur wenn location_is_defect=true ODER status="Schaden"
              const hasActiveDefect = item.standort?.location_is_defect === true || 
                                       item.standort?.status === 'Schaden';
              
              // Determine background color based on task type and ACTIVE defect status
              let bgColor = 'bg-slate-50 border-slate-200';
              if (item.needs_repair) {
                bgColor = 'bg-amber-50 border-amber-300'; // Yellow for repairs
              } else if (hasActiveDefect) {
                bgColor = 'bg-red-50 border-red-300'; // Red only for ACTIVE damage
              } else if (item.is_overdue && !snoozed) {
                bgColor = 'bg-red-50 border-red-300 animate-pulse-slow';
              } else if (item.is_soon) {
                bgColor = 'bg-amber-50 border-amber-300';
              }
              
              return (
                <div
                  key={locationId}
                  className={`p-4 rounded-xl border-2 transition-all ${bgColor}`}
                  data-testid={`task-item-${locationId}`}
                >
                  {/* Automat Auszählen Task */}
                  {item.type === 'automat_auszaehlen' && item.automat ? (
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <Euro className="text-purple-600" size={20} />
                          <Link to={`/automaten/auszaehlen-id/${item.automat.id}`} className="hover:underline">
                            <h3 className="font-bold text-lg text-purple-600 hover:text-purple-800">
                              💰 Auszählen: {item.automat.code}
                            </h3>
                          </Link>
                        </div>
                        <p className="text-sm text-slate-600 mb-2">
                          {item.automat.machine_type}
                        </p>
                        <span className="bg-purple-100 text-purple-700 px-3 py-1 rounded-full font-semibold text-sm">
                          Automat muss ausgezählt werden
                        </span>
                      </div>
                      <Button
                        onClick={() => navigate(`/automaten/auszaehlen-id/${item.automat.id}`)}
                        className="bg-purple-600 hover:bg-purple-700"
                        size="sm"
                      >
                        Jetzt auszählen
                      </Button>
                    </div>
                  ) : item.type === 'automat_defekt' && item.automat ? (
                    /* Defekter Automat Task */
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <AlertTriangle className="text-red-600" size={20} />
                          <Link to={`/automaten/${item.automat.id}`} className="hover:underline">
                            <h3 className="font-bold text-lg text-red-600 hover:text-red-800">
                              🔴 Defekt: {item.automat.code}
                            </h3>
                          </Link>
                        </div>
                        <p className="text-sm text-slate-600 mb-2">
                          {item.automat.machine_type} - Status: {item.automat.status}
                        </p>
                        {item.defect_note && (
                          <p className="text-sm text-slate-700 mb-2 bg-red-50 p-2 rounded">
                            📝 {item.defect_note}
                          </p>
                        )}
                        <span className="bg-red-100 text-red-700 px-3 py-1 rounded-full font-semibold text-sm">
                          ⚠️ Automat ist defekt - Reparatur erforderlich
                        </span>
                      </div>
                      <Button
                        onClick={() => navigate(`/automaten/${item.automat.id}`)}
                        className="bg-red-600 hover:bg-red-700"
                        size="sm"
                      >
                        Details ansehen
                      </Button>
                    </div>
                  ) : item.needs_repair && item.machine ? (
                    /* Machine Repair Task */
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <AlertTriangle className="text-amber-600" size={20} />
                          <h3 className="font-bold text-lg">🔧 Reparatur: {item.machine.code}</h3>
                        </div>
                        <p className="text-sm text-slate-600 mb-2">
                          {item.standort ? `${item.standort.name} - ${item.standort.street}` : 'Im Lager'}
                        </p>
                        <span className="bg-amber-100 text-amber-700 px-3 py-1 rounded-full font-semibold text-sm">
                          Automat benötigt Reparatur
                        </span>
                        {item.machine.defect_note && (
                          <p className="text-sm text-slate-600 mt-2">📝 {item.machine.defect_note}</p>
                        )}
                      </div>
                    </div>
                  ) : (
                    /* Standort Task */
                    <div className="flex items-start justify-between">
                      {item.standort?.primary_photo && (
                        <img 
                          src={item.standort.primary_photo} 
                          alt={item.standort.name}
                          className="w-24 h-24 object-cover rounded-lg border-2 border-slate-200"
                        />
                      )}
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <Link to={`/standorte/${item.standort?.id}`} className="hover:underline">
                            <h3 className="font-bold text-lg text-blue-600 hover:text-blue-800">
                              {item.standort?.name}
                            </h3>
                          </Link>
                          <span className="text-sm text-slate-600">{item.standort?.code}</span>
                          {item.standort?.key_number && (
                            <span className="text-sm font-semibold text-blue-600">
                              🔑 {item.standort.key_number}
                            </span>
                          )}
                          {snoozed && (
                            <span className="text-xs bg-slate-200 text-slate-700 px-2 py-1 rounded-full flex items-center space-x-1">
                              <BellOff size={12} />
                              <span>Stummgeschaltet</span>
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-slate-600 mb-2">
                          {item.standort?.street}, {item.standort?.zip} {item.standort?.city}
                        </p>
                        <div className="flex flex-wrap gap-2 text-sm">
                          {/* Aktiver Schaden: Nur rot anzeigen wenn location_is_defect=true ODER status="Schaden" */}
                          {hasActiveDefect && item.standort?.damage_note ? (
                            <span className="bg-red-100 text-red-700 px-3 py-1 rounded-full font-bold">
                              ⚠️ AKTIVER SCHADEN: {item.standort?.damage_note}
                            </span>
                          ) : item.standort?.damage_note && !hasActiveDefect ? (
                            /* Vergangener Schaden: Neutral anzeigen */
                            <span className="bg-slate-100 text-slate-600 px-3 py-1 rounded-full">
                              📋 Vergangener Schaden: {item.standort?.damage_note}
                            </span>
                          ) : item.is_empty ? (
                            <span className="bg-red-100 text-red-700 px-3 py-1 rounded-full font-bold">
                              🚨 LEER - Noch keine Automaten installiert
                            </span>
                          ) : item.last_visit_date ? (
                            <span className="bg-white px-3 py-1 rounded-full">
                              Letzter Besuch: {format(new Date(item.last_visit_date), 'dd.MM.yyyy', { locale: de })}
                            </span>
                          ) : (
                            <span className="bg-white px-3 py-1 rounded-full font-semibold">
                              Noch nie befüllt
                            </span>
                          )}
                          {item.last_leerung && (
                            <span className="bg-blue-50 text-blue-700 px-3 py-1 rounded-full">
                              Letzte Leerung: {format(new Date(item.last_leerung), 'dd.MM.yyyy', { locale: de })}
                            </span>
                          )}
                          {!hasActiveDefect && !item.is_empty && item.days_until_refill !== undefined && (
                            <span className={`px-3 py-1 rounded-full font-semibold ${
                              item.is_overdue ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                            }`}>
                              {item.is_overdue
                                ? `${Math.abs(item.days_until_refill)} Tage überfällig`
                                : `In ${item.days_until_refill} Tagen fällig`}
                            </span>
                          )}
                          {item.standort?.task && (
                            <span className="bg-purple-100 text-purple-700 px-3 py-1 rounded-full font-semibold">
                              📋 Aufgabe: {item.standort.task}
                            </span>
                          )}
                        </div>
                      </div>
                      {/* Snooze buttons for standort tasks */}
                      {item.standort && (
                        <div className="flex flex-col space-y-2">
                          {item.is_overdue && !snoozed ? (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => snoozeLocation(item.standort.id, 1)}
                                data-testid={`snooze-1day-${item.standort.code}`}
                              >
                                <Bell size={14} className="mr-1" />
                                1 Tag
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => snoozeLocation(item.standort.id, 7)}
                                data-testid={`snooze-7days-${item.standort.code}`}
                              >
                                <Bell size={14} className="mr-1" />
                                7 Tage
                              </Button>
                            </>
                          ) : snoozed ? (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => unsnoozeLocation(item.standort.id)}
                              data-testid={`unsnooze-${item.standort.code}`}
                            >
                              <BellOff size={14} className="mr-1" />
                              Aktivieren
                            </Button>
                          ) : null}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            });
          })()}
        </div>
      </Card>

      {/* QR Scanner */}
      {showScanner && (
        <QRScanner onScan={handleQRScan} onClose={() => setShowScanner(false)} />
      )}

      {/* Unknown Code Dialog - Einheitlicher Dialog für unbekannte QR-Codes */}
      <UnknownCodeDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        scannedCode={scannedCode}
        onCreateStandort={() => handleCreateNew('standort')}
        onCreateAutomat={() => handleCreateNew('automat')}
        showStandortOption={user?.role === 'admin'}
      />
    </div>
  );
};

export default DashboardPage;